import React, { useState } from 'react';
import { Button, Stack } from 'rsuite';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import FetchingSelect from '@/components/FetchingSelect';
import { BaseErrorModel } from '@/models/core.model';
import { PointAccountTargetType } from '@/models/point-account.model';
import { addPointAccountAssignment, getPointAccountsLookup } from '@/services/point-account.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import PointAccountList from '@/pages/point-account/list/point-account-list';

interface Props {
  runningClubId?: number;
}

/** Point accounts assigned to this club, plus a shortcut to assign an existing account. */
const RunningClubPointAccountList: React.FC<Props> = ({ runningClubId }) => {
  const [accountId, setAccountId] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const nav = useNavigate();

  if (!runningClubId) return null;

  const handleAssign = async () => {
    if (!accountId) return;
    setSaving(true);
    try {
      const result = await addPointAccountAssignment(accountId, { targetType: PointAccountTargetType.RunningClub, targetId: runningClubId });
      if (result.success) {
        toastSuccessMessage('Account assigned to club');
        setAccountId(undefined);
        setReloadKey(k => k + 1);
      }
    } catch (err) {
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in assigning account: ${em?.errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PointAccountList
      targetType={PointAccountTargetType.RunningClub}
      targetId={runningClubId}
      reloadKey={reloadKey}
      toolbarExtra={
        <Stack spacing={6}>
          <FetchingSelect fetchData={(text, page) => getPointAccountsLookup(text, page)} value={accountId}
            onChange={val => setAccountId(val ?? undefined)} placeholder="Pick an existing account" style={{ width: 260 }} />
          <Button appearance="primary" onClick={handleAssign} loading={saving} disabled={!accountId}>Assign existing account</Button>
          <Button appearance="ghost" onClick={() => nav('/point-accounts/new')}>New account</Button>
        </Stack>
      }
    />
  );
};

export default RunningClubPointAccountList;
